import { useQueryClient } from "@tanstack/react-query";
import {
  always,
  applySpec,
  concat,
  converge,
  either,
  evolve,
  filter,
  ifElse,
  includes,
  intersection,
  isEmpty,
  length,
  map,
  mergeAll,
  pick,
  pipe,
  prop,
  propOr,
  propSatisfies,
  unapply,
} from "ramda";
import { isString } from "../../../utils/string.utils";
import {
  Authority,
  useAuth,
  useMyMutation,
  useMyQuery,
} from "../../Authorization/AuthProvider";
import { creditFrom, toApiLocale } from "../../Team/api";

const fixAllowedCoachRates = map(
  evolve({
    allowedCoachRates: (val) => (isString(val) ? val.split(", ") : val),
  }),
);

export const useUsersQuery = ({
  showCanceled,
  primaryRolesFilter = [],
} = {}) => {
  return useMyQuery({
    queryKey: ["admin", "users", { showCanceled, primaryRolesFilter }],
    fetchDef: {
      url: `/api/latest/admin/users`,
      query: {
        size: 10000,
        sort: "username,asc",
        showCanceled: !!showCanceled, // TODO? https://github.com/top-leader-cz/top-leader-be/commit/cfd0e437e90cc9c767d4a5d934f0580e5ec9673b#diff-7bf9eae6d84de59456e3d3ccff074af470dc3659672f62bbf869eda38e5fb0baR211
      },
      to: pipe(
        prop("content"),
        fixAllowedCoachRates,
        filter(
          either(
            always(isEmpty(primaryRolesFilter)),
            pipe(
              propOr([], "primaryRoles"),
              intersection(primaryRolesFilter),
              length,
              Boolean,
            ),
          ),
        ),
      ),
    },
  });
};

export const useCompaniesQuery = (rest = {}) => {
  return useMyQuery({
    queryKey: ["companies"],
    fetchDef: { url: `/api/latest/companies` },
    ...rest,
  });
};
useCompaniesQuery.toOpts = map(
  applySpec({ value: prop("id"), label: prop("name") }),
);

export const useCompanyMutation = (rest = {}) => {
  return useMyMutation({
    fetchDef: {
      method: "POST",
      url: `/api/latest/companies`,
      from: pick(["name"]),
    },
    invalidate: [{ queryKey: ["companies"] }],
    ...rest,
  });
};

export const useCompanyEditMutation = (rest = {}) => {
  return useMyMutation({
    fetchDef: {
      method: "POST",
      getUrl: ({ name }) => `/api/latest/companies/${name}`,
      from: pick(["defaultAllowedCoachRate"]),
    },
    invalidate: [{ queryKey: ["companies"] }],
    ...rest,
  });
};

export const useAdminDeleteUserMutation = (rest = {}) => {
  return useMyMutation({
    fetchDef: {
      method: "DELETE",
      getUrl: ({ username }) => `/api/latest/admin/users/${username}`,
    },
    invalidate: [{ queryKey: ["admin", "users"] }],
    ...rest,
  });
};

export const useConfirmRequestedCreditMutation = ({
  onSuccess,
  ...params
} = {}) => {
  const { authFetch } = useAuth();
  const queryClient = useQueryClient();

  return useMyMutation({
    mutationFn: async ({ username }) =>
      authFetch({
        method: "POST",
        url: `/api/latest/admin/users/${username}/confirm-requested-credits`,
        data: {},
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin"], exact: false });
      onSuccess?.(data);
    },
    ...params,
  });
};

export const useAdminUserMutation = ({ isEdit, ...rest } = {}) => {
  return useMyMutation({
    debug: true,
    fetchDef: {
      method: "POST",
      getUrl: isEdit
        ? pipe(prop("username"), concat(`/api/latest/admin/users/`))
        : always(`/api/latest/admin/users`),
      from: converge(unapply(mergeAll), [
        pick([
          "username",
          "firstName",
          "lastName",
          "authorities",
          "timeZone",
          "companyId",
          "status",
          "allowedCoachRates",
        ]),
        applySpec({
          locale: toApiLocale,
          isTrial: ({ status }) => (status === "PENDING" ? false : true),
        }),
        ifElse(
          always(isEdit),
          applySpec({
            coach: prop("coach"),
            credit: pipe(prop("credit"), creditFrom),
            // freeCoach, // TODO?
          }),
          always({}),
        ),
        ifElse(
          propSatisfies(includes(Authority.COACH), "authorities"),
          pick(["rate", "certificate"]),
          always({}),
        ),
      ]),
    },
    invalidate: [
      { queryKey: ["hr-users"] },
      { queryKey: ["admin"], exact: false },
      { queryKey: ["user-info"] },
    ],
    ...rest,
  });
};
